import { BaseService } from '../api/base/baseService';
import { ServiceConfig } from '../api/base/types';
import { 
  SearchParams,
  SearchResult,
  ApiSearchParams
} from '../../types/search';
import { Content } from '../../types/content';
import { Topic } from '../../types/topic';
import { LiveSearchParams } from '../../types/lives';
import { createSearchParams, createQueryString } from '../../utils/api/searchUtils';
import { API_ENDPOINTS } from '../../constants/search';
import { PaginatedResponse } from '../../types/api';

export class SearchService extends BaseService {
  protected readonly resourceName = 'search';
  private static instance: SearchService;

  private constructor(config: ServiceConfig) {
    super(config);
  }

  static getInstance(config: ServiceConfig): SearchService {
    if (!SearchService.instance) {
      SearchService.instance = new SearchService(config);
    }
    return SearchService.instance;
  }

  private async executeSearch<T>(endpoint: string, params: ApiSearchParams): Promise<SearchResult<T>> {
    const queryString = createQueryString(params);
    const url = `${endpoint}${queryString}`;

    try {
      const response = await this.client.get<PaginatedResponse<T>>(url);
      
      if (!response || !Array.isArray(response.results)) {
        throw new Error('Invalid response format - expected results array');
      }

      return {
        results: response.results,
        total: response.total,
        hasMore: (params.page || 1) * (params.limit || 20) < response.total,
        page: params.page || 1
      };
    } catch (error) {
      console.error('Search error:', error);
      throw this.handleError(error);
    }
  }

  async search(params: SearchParams): Promise<SearchResult<Content | Topic>> {
    const { endpoint, params: apiParams } = createSearchParams(params);
    return this.executeSearch(endpoint, apiParams);
  }

  async searchContent(params: SearchParams): Promise<SearchResult<Content>> {
    const { params: apiParams } = createSearchParams({
      ...params,
      contentType: 'video'
    });
    return this.executeSearch<Content>(API_ENDPOINTS.CONTENT, apiParams);
  }

  async searchLives(params: LiveSearchParams): Promise<SearchResult<Content>> {
    const { params: apiParams } = createSearchParams(params);
    return this.executeSearch<Content>(API_ENDPOINTS.LIVES, apiParams);
  }

  async searchTopics(params: SearchParams): Promise<SearchResult<Topic>> {
    const { params: apiParams } = createSearchParams({
      ...params,
      contentType: 'topics'
    });
    return this.executeSearch<Topic>(API_ENDPOINTS.TOPICS, apiParams);
  }

  cancelSearch(): void {
    this.cancelAllRequests();
  }
} 